import type { CashDifferenceFilter, CashHistorySort } from '../screens/cashList.constants';
import type { CashSessionRow } from './cashSessionRow';

export type CashSessionsHistoryFilters = {
  fromDate?: string;
  toDate?: string;
  openedBy?: string | null;
  differenceFilter?: CashDifferenceFilter;
  sort?: CashHistorySort;
  limit?: number;
  offset?: number;
};

export function appendCashHistoryDifferenceSql(
  differenceFilter: CashDifferenceFilter | undefined
): string {
  switch (differenceFilter) {
    case 'exact':
      return ' AND COALESCE(difference, 0) = 0';
    case 'shortage':
      return ' AND COALESCE(difference, 0) < 0';
    case 'surplus':
      return ' AND COALESCE(difference, 0) > 0';
    case 'any':
      return ' AND COALESCE(difference, 0) <> 0';
    default:
      return '';
  }
}

export function cashHistoryOrderByClause(sort: CashHistorySort | undefined): string {
  switch (sort) {
    case 'shortage_desc':
      return ' ORDER BY COALESCE(difference, 0) ASC, closed_at DESC';
    case 'surplus_desc':
      return ' ORDER BY COALESCE(difference, 0) DESC, closed_at DESC';
    default:
      return ' ORDER BY closed_at DESC';
  }
}

export function buildCashHistoryWhereParams(
  branchId: string,
  opts?: CashSessionsHistoryFilters
): { sql: string; params: (string | number)[] } {
  let sql = ` FROM cash_sessions WHERE branch_id = ? AND status = ?`;
  const params: (string | number)[] = [branchId, 'closed'];

  // Turno solapa el rango si abrió antes del fin y cerró después del inicio.
  if (opts?.fromDate) {
    sql += ` AND closed_at >= ?`;
    params.push(opts.fromDate);
  }
  if (opts?.toDate) {
    sql += ` AND opened_at <= ?`;
    params.push(opts.toDate);
  }
  if (opts?.openedBy) {
    sql += ` AND opened_by = ?`;
    params.push(opts.openedBy);
  }
  sql += appendCashHistoryDifferenceSql(opts?.differenceFilter);

  return { sql, params };
}

export function cashSessionOpenedInRange(openedAt: string, fromIso: string, toIso: string): boolean {
  const openedMs = new Date(openedAt).getTime();
  const fromMs = new Date(fromIso).getTime();
  const toMs = new Date(toIso).getTime();
  if (!Number.isFinite(openedMs) || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) return false;
  return openedMs >= fromMs && openedMs <= toMs;
}

/** Turno abierto visible en historial cuando su apertura cae en el período y pasa filtros de operador/arqueo. */
export function openSessionMatchesHistoryQuery(
  session: CashSessionRow,
  query: CashClosuresListQuery
): boolean {
  if (session.status !== 'open') return false;
  if (!cashSessionOpenedInRange(session.openedAt, query.fromIso, query.toIso)) return false;
  if (query.openedBy && session.openedBy !== query.openedBy) return false;
  if (query.differenceFilter !== 'all') return false;
  return true;
}

export function mergeActiveSessionIntoHistoryPage(
  closedRows: CashSessionRow[],
  activeSession: CashSessionRow | null | undefined,
  query: CashClosuresListQuery,
  pageIndex: number
): CashSessionRow[] {
  if (pageIndex !== 0 || !activeSession) return closedRows;
  if (!openSessionMatchesHistoryQuery(activeSession, query)) return closedRows;
  if (closedRows.some((row) => row.id === activeSession.id)) return closedRows;
  return [activeSession, ...closedRows];
}

export function countActiveSessionInHistory(
  closedCount: number,
  closedRows: CashSessionRow[],
  activeSession: CashSessionRow | null | undefined,
  query: CashClosuresListQuery,
  pageIndex: number
): number {
  if (pageIndex !== 0 || !activeSession) return closedCount;
  if (!openSessionMatchesHistoryQuery(activeSession, query)) return closedCount;
  if (closedRows.some((row) => row.id === activeSession.id)) return closedCount;
  return closedCount + 1;
}

export type CashClosuresListQuery = {
  branchId: string;
  fromIso: string;
  toIso: string;
  openedBy: string | null;
  differenceFilter: CashDifferenceFilter;
  sort: CashHistorySort;
};

export function toCashSessionsHistoryFilters(
  query: CashClosuresListQuery,
  paging: { limit: number; offset: number }
): CashSessionsHistoryFilters {
  return {
    fromDate: query.fromIso,
    toDate: query.toIso,
    openedBy: query.openedBy,
    differenceFilter: query.differenceFilter,
    sort: query.sort,
    limit: paging.limit,
    offset: paging.offset,
  };
}

export function toCashSessionsHistoryCountFilters(
  query: CashClosuresListQuery
): Omit<CashSessionsHistoryFilters, 'limit' | 'offset' | 'sort'> {
  return {
    fromDate: query.fromIso,
    toDate: query.toIso,
    openedBy: query.openedBy,
    differenceFilter: query.differenceFilter,
  };
}
